import * as React from 'react';

import type {LeadStatus} from 'nutshell-graphql-types';
import type {LeadAutomationAssigneeOrRuleInput} from 'nutshell-graphql-types';

import {AUTO_RESOLVE_ID, SAME_ASSIGNEE_ID} from '../../../core/form/fields/consts';

import {ClosedLeadFollowupRuleForm} from '../../forms/closed-lead-followup-rule-form';
import type {FormData} from '../../forms/closed-lead-followup-rule-form';

import {
    useGetClosedLeadFollowupAutomation,
    useCreateClosedLeadFollowupAutomation,
    useDeleteClosedLeadFollowupAutomation,
    useUpdateClosedLeadFollowupAutomation,
} from './graphql/hooks';
import {SalesAutomationEditorSidebar} from './sidebar/sales-automation-editor-sidebar';

type Props = {
    parentId: string;
    stagesetId: string;
    entityId?: string;
};

export function ClosedLeadFollowupRuleEditor(props: Props) {
    const {closedLeadFollowupAutomation, isLoading} = useGetClosedLeadFollowupAutomation({
        automationId: props.entityId,
    });
    const {createClosedLeadFollowupAutomation} = useCreateClosedLeadFollowupAutomation();
    const {deleteClosedLeadFollowupAutomation} = useDeleteClosedLeadFollowupAutomation();
    const {updateClosedLeadFollowupAutomation} = useUpdateClosedLeadFollowupAutomation();

    const formSubmitBtnRef = React.useRef<HTMLInputElement | null | undefined>(null);

    const handleFormSave = () => {
        if (formSubmitBtnRef.current) {
            formSubmitBtnRef.current.click();
        }
    };

    const handleSubmit = (formData: FormData) => {
        // This is a refinement for flow, to guarantee that the parentId is an
        // expected outcomeType
        if (
            props.parentId !== 'LEAD_CANCELLED' &&
            props.parentId !== 'LEAD_WON' &&
            props.parentId !== 'LEAD_LOST'
        ) {
            return Promise.resolve();
        }

        if (props.entityId) {
            return updateClosedLeadFollowupAutomation({
                actionId: props.entityId,
                newStagesetId: formData.pipelineId,
                assigneeOrRule: getAssigneeIdOrRule(formData.assigneeId),
                tagIds: formData.tags,
            });
        }

        const status = getStatusFromParentId(props.parentId);

        return createClosedLeadFollowupAutomation({
            stagesetId: props.stagesetId,
            status,
            newStagesetId: formData.pipelineId,
            assigneeOrRule: getAssigneeIdOrRule(formData.assigneeId),
            tagIds: formData.tags,
        });
    };

    const handleDelete = () => {
        if (props.entityId) {
            return deleteClosedLeadFollowupAutomation({
                id: props.entityId,
            });
        }

        return Promise.resolve();
    };

    return (
        <>
            {(!props.entityId || !isLoading) && (
                <SalesAutomationEditorSidebar
                    onSave={handleFormSave}
                    title={
                        props.entityId ? 'Edit follow-up lead rule' : 'Create follow-up lead rule'
                    }
                >
                    <ClosedLeadFollowupRuleForm
                        formData={closedLeadFollowupAutomation}
                        getFormSubmitBtnRef={(node) => {
                            formSubmitBtnRef.current = node;
                        }}
                        status={getStatusFromParentId(props.parentId)}
                        onDelete={props.entityId ? handleDelete : undefined}
                        onSubmit={handleSubmit}
                    />
                </SalesAutomationEditorSidebar>
            )}
        </>
    );
}

function getAssigneeIdOrRule(assigneeId: string): LeadAutomationAssigneeOrRuleInput {
    if (assigneeId === AUTO_RESOLVE_ID) {
        return {assignmentRule: 'AUTO_ASSIGN'};
    }

    if (assigneeId === SAME_ASSIGNEE_ID) {
        return {assignmentRule: 'ASSIGN_TO_SOURCE_LEAD_ASSIGNEE'};
    }

    return {assignee: assigneeId};
}

function getStatusFromParentId(parentId: string): LeadStatus {
    switch (parentId) {
        case 'LEAD_CANCELLED':
            return 'CANCELLED';
        case 'LEAD_WON':
            return 'WON';
        case 'LEAD_LOST':
        default:
            return 'LOST';
    }
}
